import { LightWeightObjectChangeTracker, getAllLeafPaths } from '@schoolbelle/common/object-change-tracker';
import {
  FirebaseDatabaseStub,
  AngularFirebaseDatabaseStub
} from './AngularFirebaseDatabaseStub';
import { get, difference, intersection, uniqBy } from 'lodash';
import { TransactionServiceStub } from './TransactionServiceStub';
import * as _firebase from 'firebase/app';
const firebase = _firebase;

export type DataChanges = {
  deletes: { path: string; from: any }[];
  inserts: { path: string; to: any }[];
  updates: { path: string; from: any; to: any }[];
};

export class FirebaseFunctionsStub {
  private history: any[] = [{}];
  get from() {
    return this.history[this.history.length - 2];
  }
  get to() {
    return this.history[this.history.length - 1];
  }
  private changes: DataChanges;
  private checkPathAndMoreRegex: RegExp;
  constructor(
    private tracker: LightWeightObjectChangeTracker,
    private db: AngularFirebaseDatabaseStub,
    private transaction: TransactionServiceStub
  ) {
    this.checkPathAndMoreRegex = /^\/transaction\/([^\/]+)\/check\/([^\/]+)\/([^\/]+)\/([^\/]+)/;
    tracker.onContentUpdate.subscribe(() => {
      this.recordInHistory();
      this.updateChanges(this.from, this.to);
      uniqBy(
        this.changes.inserts.filter(
          item => (item.path.match(this.checkPathAndMoreRegex) ? true : false)
        ),
        item => item.path.match(this.checkPathAndMoreRegex)[0]
      ).forEach(ins => this.onCheckCreation(ins));
      uniqBy(
        this.changes.updates.filter(
          item => (item.path.match(this.checkPathAndMoreRegex) ? true : false)
        ),
        item => item.path.match(this.checkPathAndMoreRegex)[0]
      ).forEach(upd => this.onCheckUpdate(upd));
      uniqBy(
        this.changes.deletes.filter(
          item => (item.path.match(this.checkPathAndMoreRegex) ? true : false)
        ),
        item => item.path.match(this.checkPathAndMoreRegex)[0]
      ).forEach(del => this.onCheckDeletion(del));
    });

    // setInterval(()=>{
    //     this.runGarbadgeCollections();
    // }, 10*60*1000)
  }
  private recordInHistory() {
    this.history.push({ ...this.tracker.value });
  }
  private updateChanges(from: any, to: any): void {
    let fromLeaves = getAllLeafPaths(from);
    let toLeaves = getAllLeafPaths(to);
    this.changes = {
      deletes: difference(fromLeaves, toLeaves).map(path => {
        return { path, from: get(from, path) };
      }),
      inserts: difference(toLeaves, fromLeaves).map(path => {
        return { path, to: get(to, path) };
      }),
      updates: intersection(fromLeaves, toLeaves)
        .filter(path => {
          return (
            get(from, path.split('/').slice(1)) !==
            get(to, path.split('/').slice(1))
          );
        })
        .map(path => {
          return { path, from: get(from, path), to: get(to, path) };
        })
    };
  }
  private onCheckCreation(item: { path: string; to?: any; from?: any }) {
    const path = item.path.match(this.checkPathAndMoreRegex)[0];
    const afterVal = get(this.to, path.split('/').slice(1));
    const checkType = afterVal.type;
    const URIencodedPath = encodeURIComponent(path);
    if (checkType === 'reserving') {
      this.withDiff(this.getQuotaRef(this.db.database as any, path), 1).then(
        () => {
          return Promise.all([
            this.transaction.fromAToB(
              this.db.database.ref(`${path}/type`),
              'reserving',
              'reserved'
            ),
            this.addGarbadgeCollection({
              path: URIencodedPath,
              handle_at: Date.now() + (afterVal.collectIn || 10 * 60 * 1000),
              transactionKey: 'type',
              from: 'reserved',
              to: 'unreserving',
              onTransactionSuccess: 'none'
            })
          ]);
        }
      );
    }
  }
  private onCheckUpdate(item: { path: string; to?: any; from?: any }) {
    const path = item.path.match(this.checkPathAndMoreRegex)[0];
    const afterVal = get(this.to, path.split('/').slice(1));
    const beforeVal = get(this.from, path.split('/').slice(1));
    const URIencodedPath = encodeURIComponent(path);
    const checkRef = this.db.database.ref(path);

    const afterCheckType = afterVal.type;
    const beforeCheckType = beforeVal.type;
    switch (`${beforeCheckType}->${afterCheckType}`) {
      case 'reserved->unreserving':
        return Promise.all([
          this.removeGarbadgeCollection(URIencodedPath), // deregister garbage collection
          checkRef.remove() // remove check
        ]);
      case 'confirmed-to-be-canceled->unconfirming':
        return checkRef.remove(); // remove check
      case 'reserved->confirmed':
      case 'confirmed-to-be-canceled->confirmed':
        return this.removeGarbadgeCollection(URIencodedPath); // deregister garbage collection
      case 'confirmed->confirmed-to-be-canceled':
        return this.addGarbadgeCollection({
          path: URIencodedPath,
          handle_at: Date.now() + (afterVal.collectIn || 10 * 60 * 1000),
          transactionKey: 'type',
          from: 'confirmed-to-be-canceled',
          to: 'confirmed',
          onTransactionSuccess: 'none'
        }); // register garbage collection
      default:
        return null;
    }
  }
  private onCheckDeletion(item: { path: string; to?: any; from?: any }) {
    const path = item.path.match(this.checkPathAndMoreRegex)[0];
    const beforeVal = get(this.from, path.split('/').slice(1));
    const checkType = beforeVal.type;
    const quotaRef = this.getQuotaRef(this.db.database, path);
    switch (checkType) {
      case 'unreserving':
      case 'unconfirming':
        return this.withDiff(quotaRef, -1); // race quota --
      default:
        return null;
    }
  }
  private getQuotaRef(
    db: FirebaseDatabaseStub,
    path: string
  ): firebase.database.Reference {
    let match = path.match(this.checkPathAndMoreRegex);
    const survey_id = match[1],
      question_id = match[2],
      option_id = match[3];
    return db.ref(
      `/transaction/${survey_id}/filled_up_to/${question_id}/${option_id}`
    );
  }
  withDiff(
    refOrPath: string | firebase.database.Reference,
    diff: number
  ): Promise<any> {
    const ref =
      typeof refOrPath === 'string'
        ? this.db.database.ref(refOrPath)
        : refOrPath;
    return ref.transaction(function(currentData) {
      let new_value = (currentData || 0) + diff;
      return new_value;
    });
  }

  private addGarbadgeCollection(garbage: any) {}
  private removeGarbadgeCollection(key: string) {}
  private runGarbadgeCollections() {}
}
